import { useState, useCallback } from 'react';
import type { MatchResult } from '../types';
import { formatResultsAsText } from '../lib/formatResults';

interface ResultsActionButtonsProps {
  data: MatchResult;
  resultsUrl: string;
}

type SaveFeedback = 'idle' | 'shared' | 'copied';
type ShareFeedback = 'idle' | 'shared' | 'link-copied';

export default function ResultsActionButtons({ data, resultsUrl }: ResultsActionButtonsProps) {
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback>('idle');
  const [shareFeedback, setShareFeedback] = useState<ShareFeedback>('idle');
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleSave = useCallback(async () => {
    const text = formatResultsAsText(data, resultsUrl);
    if (canShare) {
      try {
        await navigator.share({ title: '🔥 OurSexQuiz Results', text });
        window.umami?.track('results-saved');
        window.AhrefsAnalytics?.sendEvent('results-saved');
        setSaveFeedback('shared');
        setTimeout(() => setSaveFeedback('idle'), 2000);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        try {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        } catch {
          return;
        }
      }
      window.umami?.track('results-saved');
      window.AhrefsAnalytics?.sendEvent('results-saved');
      setSaveFeedback('copied');
      setTimeout(() => setSaveFeedback('idle'), 2000);
    }
  }, [data, resultsUrl, canShare]);

  const handleShare = useCallback(async () => {
    if (canShare) {
      try {
        await navigator.share({ title: '🔥 OurSexQuiz Results', url: resultsUrl });
        window.umami?.track('results-shared');
        window.AhrefsAnalytics?.sendEvent('results-shared');
        setShareFeedback('shared');
        setTimeout(() => setShareFeedback('idle'), 2000);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    } else {
      try {
        await navigator.clipboard.writeText(resultsUrl);
      } catch {
        return;
      }
      window.umami?.track('results-shared');
      window.AhrefsAnalytics?.sendEvent('results-shared');
      setShareFeedback('link-copied');
      setTimeout(() => setShareFeedback('idle'), 2000);
    }
  }, [canShare, resultsUrl]);

  const baseClass = 'btn-glass inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors';
  const idleClass = 'text-slate-300';
  const successClass = 'bg-green-500/10 border-green-500/30 text-green-400';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap justify-center gap-3">
        {/* Save Results */}
        <button
          onClick={handleSave}
          className={`${baseClass} ${saveFeedback !== 'idle' ? successClass : idleClass}`}
        >
          <span aria-live="polite" aria-atomic="true" className="inline-flex items-center justify-center gap-2">
            {saveFeedback === 'shared' && '✓ Shared!'}
            {saveFeedback === 'copied' && '✓ Copied!'}
            {saveFeedback === 'idle' && (
              <>
                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                </svg>
                Save Results
              </>
            )}
          </span>
        </button>

        {/* Copy Private Link */}
        <button
          onClick={handleShare}
          className={`${baseClass} ${shareFeedback !== 'idle' ? successClass : idleClass}`}
        >
          <span aria-live="polite" aria-atomic="true" className="inline-flex items-center justify-center gap-2">
            {shareFeedback === 'shared' && '✓ Shared!'}
            {shareFeedback === 'link-copied' && '✓ Link copied!'}
            {shareFeedback === 'idle' && (
              <>
                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                </svg>
                Copy Private Link
              </>
            )}
          </span>
        </button>
      </div>

      <p className="mx-auto max-w-md text-center text-xs text-slate-500 text-pretty">
        We delete all data after 30 days. Save your results while they&apos;re hot!
      </p>
    </div>
  );
}
